import { BULLET_RADIUS, TANK_RADIUS, VISION_RADIUS } from "../../shared/constants.js";
import type {
  FogVisionMode,
  HazardZoneDTO,
  KillEvent,
  MazeDTO,
  PowerupDTO,
  PowerupType,
  SnapshotDTO,
  SpawnZoneDTO,
} from "../../shared/protocol/index.js";
import { state } from "../state.js";
import type { Beam, Buffered, Explosion, Particle, Ring, TankFx } from "./types.js";
import { render } from "./frame.js";
import { interpolated, push, takeEvents } from "./snapshots.js";

/**
 * The client's canvas renderer. This class owns the drawing state (snapshot
 * buffer, transient effects, fog/wall config, canvas + patterns); the drawing
 * and interpolation logic lives in the sibling modules of this package as
 * functions over this state (frame, fog, world, tanks/, effects, scope, …).
 */
export class Renderer {
  ctx: CanvasRenderingContext2D;
  fogOverlay: HTMLCanvasElement | null = null;
  fogOverlayCtx: CanvasRenderingContext2D | null = null;
  maze: MazeDTO | null = null;
  worldWidth = 0;
  worldHeight = 0;
  dpr = 1;
  // Upper bound on devicePixelRatio (render-quality setting). Lower = fewer
  // pixels to fill each frame = less CPU/GPU, at the cost of some sharpness.
  maxDpr = Infinity;
  spawnZones: SpawnZoneDTO[] = [];
  buffer: Buffered[] = [];
  explosions: Explosion[] = [];
  beams: Beam[] = [];
  particles: Particle[] = [];
  rings: Ring[] = [];
  // Per-tank transient effects, keyed by tank id.
  tankFx = new Map<string, TankFx>();
  // Last position/time a tank kicked up a dust puff (movement trail throttle).
  lastDust = new Map<string, { x: number; y: number; at: number }>();
  // Tread/wheel scroll: last drawn position + accumulated signed distance
  // rolled along each tank's own forward axis (see tankTreadPhase in util.ts).
  lastTreadPos = new Map<string, { x: number; y: number }>();
  treadPhase = new Map<string, number>();
  // Screen-shake magnitude in world px; decays exponentially each frame.
  shakeMag = 0;
  // Previous render() timestamp for per-frame particle integration.
  lastFrameAt = 0;
  // Wall indices currently at 0 HP, to fire debris exactly on the break frame.
  brokenWalls = new Set<number>();
  // Per-game visual sizes (from the lobby's advanced config).
  tankR = TANK_RADIUS;
  bulletR = BULLET_RADIUS;
  // Bullet-physics params for the local-only scope (aiming guide). Each weapon's
  // guide reach + behavior is derived from these (real max travel = speed × life).
  scope = {
    bulletSpeed: 240,
    bulletLifetime: 5,
    bulletBounces: 3,
    sniperSpeedMult: 5,
    sniperWallPierce: 10,
    trackingLifetime: 6,
    trackingBounces: 6,
    laserRange: 1320,
    explosionRadius: 56,
    multiCount: 3,
    multiSpread: 30,
  };
  // Last seen state per tank, to detect deaths and spawn explosions.
  lastTankState = new Map<string, { x: number; y: number; alive: boolean; hp: number; color: string; ammo: number; weaponChargeSum: number; hadRapid: boolean; reloadIn: number; boosted: boolean; shielded: boolean; scoped: boolean; charging: boolean; weaponCharges: Partial<Record<PowerupType, number>> }>();
  lastPowerups = new Map<number, PowerupDTO>();
  brickPattern: CanvasPattern | null = null;
  steelPattern: CanvasPattern | null = null;
  // Pre-rendered default-skin floor (parchment + washes + ruling + decor).
  // Rebuilt when the maze size or dpr changes; drawn as one image per frame.
  floorCache: HTMLCanvasElement | null = null;
  floorCacheKey = "";
  // Bullet ids seen last frame, to detect rapid-fire's scheduled shots — they
  // add a bullet without changing ammo/weaponCharges (only the initiating
  // click does), so the ammo/charge-delta check below can't see them.
  lastBulletIds = new Set<number>();
  // Transient effects (explosions, beams, kill events) are applied on the same
  // ~INTERP_DELAY-behind clock as the interpolated world, so a hit/death shows
  // exactly when the (delayed) bullet reaches the tank — not when the snapshot
  // arrives. `consumedUntil` is the recvAt of the last snapshot whose effects
  // we've fired; `pendingEvents` queues kill-log events for the main loop.
  consumedUntil = 0;
  pendingEvents: KillEvent[] = [];
  displayedSnap: SnapshotDTO | null = null;
  // Fog of war: when on, non-wall visuals are clipped to team reveal sources.
  // Enemy tanks also need line of sight unless scope grants x-ray. Client-side
  // only — the server still broadcasts all entities.
  fogOfWar = false;
  visionRadius = VISION_RADIUS;
  fogBaseVision: FogVisionMode = "team";
  fogFlagVision: FogVisionMode = "team";
  fogHideCarriedFlag = true;
  // Hazard zones: terrain tiles (lava/mud/ice/heal) drawn under the walls.
  hazards: HazardZoneDTO[] = [];
  // Destructible walls: per-wall HP array (index matches MazeDTO.walls). Walls
  // at 0 HP are destroyed (skipped in draw + collision). Updated from snapshot
  // `wallHp` deltas. Empty when destructibleWalls is off.
  wallHp: number[] = [];
  wallMaxHp = Infinity;
  destructibleWalls = false;

  constructor(public canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2D canvas context unavailable");
    this.ctx = ctx;
  }

  setParams(tankRadius: number, bulletRadius: number): void {
    this.tankR = tankRadius;
    this.bulletR = bulletRadius;
  }

  /** Bullet-physics params used to draw the aiming guide. */
  setScope(p: {
    bulletSpeed: number;
    bulletLifetime: number;
    bulletBounces: number;
    sniperSpeedMult: number;
    sniperWallPierce: number;
    trackingLifetime: number;
    trackingBounces: number;
    laserRange: number;
    explosionRadius: number;
    multiCount: number;
    multiSpread: number;
  }): void {
    this.scope = { ...p };
  }

  /** Team VS designated spawn areas; empty in other modes / when disabled. */
  setSpawnZones(zones: SpawnZoneDTO[]): void {
    this.spawnZones = zones;
  }

  /** Configure fog of war for the current game. */
  setFog(
    fogOfWar: boolean,
    visionRadius: number,
    fogBaseVision: FogVisionMode,
    fogFlagVision: FogVisionMode,
    fogHideCarriedFlag: boolean
  ): void {
    this.fogOfWar = fogOfWar;
    this.visionRadius = visionRadius;
    this.fogBaseVision = fogBaseVision;
    this.fogFlagVision = fogFlagVision;
    this.fogHideCarriedFlag = fogHideCarriedFlag;
  }

  /** Hazard zones for the current game (lava/mud/ice/heal terrain tiles). */
  setHazards(zones: HazardZoneDTO[]): void {
    this.hazards = zones;
  }

  /** Configure destructible walls. Initializes the per-wall HP array from the
   *  maze if one is already set; otherwise stores the config for setMaze. */
  setDestructibleWalls(enabled: boolean, wallHp: number): void {
    const sameConfig = this.destructibleWalls === enabled && this.wallMaxHp === (enabled ? wallHp : Infinity);
    this.destructibleWalls = enabled;
    this.wallMaxHp = enabled ? wallHp : Infinity;
    if (enabled && this.maze && (!sameConfig || this.wallHp.length !== this.maze.walls.length)) {
      this.wallHp = new Array(this.maze.walls.length).fill(wallHp);
    } else if (!enabled) {
      this.wallHp = [];
    }
  }

  setMaze(maze: MazeDTO): void {
    this.maze = maze;
    this.resizeDrawingBuffer(maze.width, maze.height);
    this.buffer = [];
    this.explosions = [];
    this.beams = [];
    this.particles = [];
    this.rings = [];
    this.tankFx.clear();
    this.lastDust.clear();
    this.lastTreadPos.clear();
    this.treadPhase.clear();
    this.shakeMag = 0;
    this.brokenWalls.clear();
    this.lastTankState.clear();
    this.lastPowerups.clear();
    this.lastBulletIds.clear();
    this.consumedUntil = 0;
    this.pendingEvents = [];
    this.displayedSnap = null;
    // Initialize per-wall HP from the destructible-walls config.
    this.wallHp = this.destructibleWalls
      ? new Array(maze.walls.length).fill(this.wallMaxHp)
      : [];
  }

  /** Buffer a snapshot. Effects are NOT fired here — they're applied later, when
   *  the interpolation clock reaches this snapshot (see snapshots.ts). */
  push(snap: SnapshotDTO, nowMs: number): void {
    push(this, snap, nowMs);
  }

  /** Drain kill-log events that have reached the interpolation clock. */
  takeEvents(): KillEvent[] {
    return takeEvents(this);
  }

  /** The interpolated snapshot drawn last frame (the on-screen world). */
  displayed(): SnapshotDTO | null {
    return this.displayedSnap;
  }

  /** Most recent snapshot — used for authoritative HUD/score and local aim. */
  latest(): SnapshotDTO | null {
    return this.buffer.length ? this.buffer[this.buffer.length - 1].snap : null;
  }

  render(localId: string, nowMs: number): void {
    render(this, localId, nowMs);
  }

  /** Linearly interpolate tank/bullet positions in the recent past. */
  interpolated(nowMs: number): SnapshotDTO | null {
    return interpolated(this, nowMs);
  }

  /**
   * Backing-store pixels scale with DPR for sharp Retina rendering, but all draw
   * calls stay in maze/world coordinates through the canvas transform. Input code
   * reads the stored world size so mouse aiming is not multiplied by DPR.
   */
  resizeDrawingBuffer(worldWidth: number, worldHeight: number): void {
    this.worldWidth = worldWidth;
    this.worldHeight = worldHeight;
    this.dpr = this.currentDpr();
    this.canvas.width = Math.round(worldWidth * this.dpr);
    this.canvas.height = Math.round(worldHeight * this.dpr);
    this.canvas.dataset.worldWidth = String(worldWidth);
    this.canvas.dataset.worldHeight = String(worldHeight);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  currentDpr(): number {
    if (state.retroEnabled) return 0.25;
    const dpr = Math.max(1, Math.min(this.maxDpr, window.devicePixelRatio || 1));
    // fitCanvas stretches the element via CSS, so on small arenas / large
    // screens the on-screen size can far exceed the world size — scale the
    // backing store up to match the displayed size or everything blurs.
    // (clientWidth is 0 before first layout → fall back to no upscale.)
    const upscale =
      this.worldWidth > 0 && this.canvas.clientWidth > 0
        ? Math.max(1, this.canvas.clientWidth / this.worldWidth)
        : 1;
    // Keep the backing store sane: ≤ 4096 px on the long edge.
    const maxScale = 4096 / Math.max(1, this.worldWidth, this.worldHeight);
    return Math.min(dpr * upscale, Math.max(1, maxScale));
  }

  /** Set the render-quality DPR cap and re-apply it to the current canvas. */
  setMaxDpr(maxDpr: number): void {
    this.maxDpr = maxDpr;
    if (this.maze) this.resizeDrawingBuffer(this.worldWidth, this.worldHeight);
  }
}
